const finite = (value) => typeof value === 'number' && Number.isFinite(value);
// Mirror the existing raster colour buckets, not gaps between populated classes.
// Changing a label must never regroup pixels or recompute the prescribed doses.
export function fertilizerClassWidth(band) {
    if (band === 4)
        return 5;
    if (band === 1 || band === 5)
        return 0.01;
    if ([2, 6, 7, 8].includes(band))
        return 0.1;
    if (band === 3 || band === 9)
        return 1;
    return null;
}
export function legendNumber(value, digits = 2) {
    if (!finite(value))
        return '—';
    const fixed = value.toFixed(digits);
    return (Number(fixed) === 0 ? (0).toFixed(digits) : fixed).replace('.', ',');
}
export function legendShare(value) {
    if (!finite(value))
        return '—';
    return value > 0 && value < 0.1 ? '<0,1%' : `${legendNumber(value, 1)}%`;
}
export function fertilizerLegendRows(items, band, area) {
    const width = fertilizerClassWidth(band);
    const digits = width === 0.01 ? 2 : width === 0.1 ? 1 : 0;
    return items.filter((item) => finite(item.value) && finite(item.ha) && item.ha >= 0)
        .slice().sort((left, right) => left.value - right.value)
        .map((item) => {
        // Other bands use truncation, so negative classes have the opposite
        // inclusive boundary. Normally concentrations and rates are nonnegative.
        const negativeTruncation = band !== 4 && item.value < 0;
        const low = width === null ? item.value : negativeTruncation ? item.value - width : item.value;
        const high = width === null ? null : negativeTruncation ? item.value : item.value + width;
        const share = finite(area) && area > 0 ? item.ha / area * 100 : null;
        return Object.assign(Object.assign({}, item), { range: high === null ? legendNumber(low) : `${legendNumber(low, digits)}–${legendNumber(high, digits)}`, boundaryNote: high === null ? 'Значение класса карты.' : negativeTruncation
                ? 'Нижняя граница не включена, верхняя включена.'
                : 'Нижняя граница включена, верхняя не включена.', share: finite(share) ? share : null });
    });
}
// This is a presentation summary of existing classes, not new map classes or
// prescription zones. The original rows stay available without modification.
export function fertilizerLegendSummary(rows, band, area) {
    const width = fertilizerClassWidth(band);
    if (!width || !rows.length || (band !== 4 && rows.some((row) => row.value < 0)))
        return null;
    const ordered = rows.slice().sort((left, right) => left.value - right.value);
    const ticks = ordered.map((row) => Math.round(row.value / width));
    const first = ticks[0];
    const end = ticks[ticks.length - 1] + 1;
    const span = end - first;
    if (!Number.isSafeInteger(first) || !Number.isSafeInteger(end) || !Number.isSafeInteger(span) || span <= 0)
        return null;
    const count = Math.min(6, span);
    const digits = width === 0.01 ? 2 : width === 0.1 ? 1 : 0;
    const result = [];
    for (let index = 0; index < count; index++) {
        const low = first + Math.floor(index * span / count);
        const high = index === count - 1 ? end : first + Math.floor((index + 1) * span / count);
        const members = ordered.filter((_, memberIndex) => ticks[memberIndex] >= low && ticks[memberIndex] < high);
        if (!members.length)
            continue;
        const ha = members.reduce((sum, row) => sum + row.ha, 0);
        const summ = members.every((row) => finite(row.summ)) ? members.reduce((sum, row) => sum + row.summ, 0) : null;
        const share = finite(area) && area > 0 ? ha / area * 100 : null;
        if (!finite(ha))
            return null;
        const colors = members.map((row) => row.color).filter((color, colorIndex, source) => colorIndex === 0 || color !== source[colorIndex - 1]);
        result.push({
            value: low * width, ha, summ: finite(summ) ? summ : null, color: colors[0], colors,
            classCount: members.length,
            range: `${legendNumber(low * width, digits)}–${legendNumber(high * width, digits)}`,
            boundaryNote: 'Нижняя граница включена, верхняя не включена.',
            share: finite(share) ? share : null,
        });
    }
    return result;
}
export function fertilizerLegendPage(rows, requestedPage, pageSize = 20) {
    const size = Number.isSafeInteger(pageSize) && pageSize > 0 ? pageSize : 20;
    const pageCount = Math.max(1, Math.ceil(rows.length / size));
    const page = finite(requestedPage) ? Math.min(pageCount - 1, Math.max(0, Math.floor(requestedPage))) : 0;
    return { items: rows.slice(page * size, (page + 1) * size), page, pageCount,
        from: rows.length ? page * size + 1 : 0, to: Math.min((page + 1) * size, rows.length), total: rows.length };
}
// Hard stops retain the exact server colours; there is no interpolation or
// replacement palette which could imply different colours on the actual map.
export function fertilizerLegendColorStrip(colors) {
    if (colors.length < 2 || !colors.every((color) => /^#[0-9a-f]{3,8}$/i.test(color)))
        return undefined;
    return `linear-gradient(90deg, ${colors.map((color, index) => `${color} ${index / colors.length * 100}%, ${color} ${(index + 1) / colors.length * 100}%`).join(', ')})`;
}
